// The one drawer every lettings record uses (docs/plans/lettings-one-form.md; reworked 26 Oct 2026, later — "buttons at the
// bottom, no card that opens, no modal on top"): eyebrow (kind · reference), the person as the title, home · when under
// it, the status chip on the right, the body, and the footer bar at the bottom with the actions the state allows.
// An action is a **step inside the same drawer**: its footer button swaps the body for the step (a short heading, its
// fields, nothing else) and the footer becomes Back + the confirming button(s) — DrawerStep. Steps that tell the person
// have one button with the verb; pressing it asks "Would you like to tell them?" — Email · Text · Both · Don't tell them
// (TellButtons, Tell = EMAIL | SMS | BOTH | null, channelsOf). Messages go through POST /lettings/notify (sendNote /
// tellNote, one outbox item per channel in TellResult.notifications; SMS waits in the outbox as NOT_CONFIGURED until a
// provider is wired, NotifyOutcome says so, one line per channel). The footer is a slot (Sheet footerRef) the body portals into (DrawerFooter), so each step owns
// its own buttons and state. Also here: KV, FieldTable (the Overviews' one label | value table, two side by side), Section,
// ReadOnlyForm (the application form JSON read-only, section by section; readOnlyFormItems is the same as a flat list), SlotChip, MessageStep (free text / a template, "Message them…" on closed records and Ping on Offers).
// After an action (3 Nov 2026 — "no Back to the record screen") the drawer goes straight back to the record: what was done
// and what the outbox said about the message show as a notice bar at the top of the body (useNotice / NoticeBar), which
// clears on ✕, on the next action, or by itself after a few seconds. DoneStep stays for a step inside a step (Ping).
import { Fragment, createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, Field, Input, Select, Sheet, Textarea } from '../ui';
import { FormError } from '../forms/common';
import { useAuth } from '../../stores/auth';
import { templatesApi, type EmailTemplate, type OutboxItem } from '../../lib/comms';
import { notifyApi, gbp, type NotifyAbout, type NotifyChannel } from '../../lib/lettings';
import { EMPLOYMENT_STATUSES, slotLabel } from '../../lib/applicationForm';
import { fmtDate, fmtDay, fmtStamp } from '../../lib/dates';
import { formatPhone } from '../../lib/phone';

// ── The drawer ─────────────────────────────────────────────────────────────────

const FooterSlot = createContext<HTMLElement | null>(null);

export interface DrawerNavItem { key: string; label: string; count?: string | number | null }
/** The sections of a record as a sidebar inside the drawer (the app's own sidebar look — smaksly): a strip of pills on a phone. */
export interface DrawerNav { items: DrawerNavItem[]; value: string; onChange: (key: string) => void }

export function RecordDrawer({ open, onClose, eyebrow, title, subtitle, status, below, nav, children, size = 'lg' }: {
  open: boolean; onClose: () => void; eyebrow?: ReactNode; title: ReactNode; subtitle?: ReactNode;
  /** The status chip, at the right of the header. */ status?: ReactNode;
  /** Under the header, outside the scroll (a tab strip). */ below?: ReactNode;
  /** A sidebar of sections at the left of the body (Overview · Documents · Move-in); absent while an action step is open. */ nav?: DrawerNav;
  children: ReactNode; size?: 'sm' | 'md' | 'lg' | 'xl';
}) {
  const [slot, setSlot] = useState<HTMLDivElement | null>(null);
  return (
    <Sheet open={open} onClose={onClose} size={size} eyebrow={eyebrow} title={title} subtitle={subtitle} headerExtra={status} below={below} footerRef={setSlot}
      padded={!nav} label={typeof title === 'string' ? title : undefined}>
      <FooterSlot.Provider value={slot}>
        {nav ? (
          <div className="flex min-h-full flex-col sm:flex-row">
            <nav aria-label="Sections" className="shrink-0 border-b border-ink-100 bg-ink-50/60 px-2 py-2 sm:w-44 sm:border-b-0 sm:border-r sm:py-3">
              <ul className="flex gap-1 overflow-x-auto sm:flex-col sm:gap-0">
                {nav.items.map((it) => {
                  const on = it.key === nav.value;
                  return (
                    <li key={it.key} className="shrink-0">
                      <button type="button" onClick={() => nav.onChange(it.key)} aria-current={on ? 'page' : undefined}
                        className={`relative mb-px flex w-full items-center gap-2 whitespace-nowrap rounded-lg px-2.5 py-[6px] text-left text-[12.5px] transition ${on ? 'bg-bronze-50 font-semibold text-bronze-600' : 'font-medium text-ink-500 hover:bg-ink-100 hover:text-ink-900'}`}>
                        {on && <span aria-hidden className="absolute bottom-1.5 left-0 top-1.5 hidden w-[3px] rounded-r bg-bronze-500 sm:block" />}
                        <span className="min-w-0 flex-1 truncate">{it.label}</span>
                        {it.count !== undefined && it.count !== null && it.count !== '' && <span className={`rounded-full px-1.5 text-[10.5px] font-semibold tabular-nums ${on ? 'bg-bronze-100 text-bronze-700' : 'bg-ink-100 text-ink-600'}`}>{it.count}</span>}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </nav>
            <div className="min-w-0 flex-1 px-4 py-4 sm:px-5">{children}</div>
          </div>
        ) : children}
      </FooterSlot.Provider>
    </Sheet>
  );
}

/** Buttons for the drawer's footer bar (right-aligned; a Back button sits on the left). Render one at a time. */
export function DrawerFooter({ children }: { children: ReactNode }) {
  const el = useContext(FooterSlot);
  return el ? createPortal(children, el) : null;
}

/**
 * One step of an action, in place of the drawer's body: a heading, an optional line under it, the step's fields; the footer
 * is Back + the step's buttons. `back` false hides Back (a result screen has its own button).
 */
export function DrawerStep({ title, intro, onBack, actions, children, back = true }: {
  title: string; intro?: ReactNode; onBack: () => void; actions?: ReactNode; children?: ReactNode; back?: boolean;
}) {
  return (
    <div>
      <h3 className="text-[15px] font-semibold text-ink-900">{title}</h3>
      {intro && <div className="mt-1 text-[12.5px] leading-relaxed text-ink-600">{intro}</div>}
      {children && <div className="mt-4 space-y-3.5">{children}</div>}
      <DrawerFooter>
        {back && <Button variant="secondary" className="mr-auto" onClick={onBack}>Back</Button>}
        {actions}
      </DrawerFooter>
    </div>
  );
}

/**
 * A block of the drawer's body: its title, a thin rule, an optional button at the right. Without a title (3 Nov 2026 — "remove
 * the titles, never the content"; later the same day — "and the lines they left") it draws no rule, only the space between
 * groups; a button still sits on the right.
 */
export const Section = ({ title, children, aside }: { title?: string; children: ReactNode; aside?: ReactNode }) => (
  <section className="mt-5 first:mt-0">
    {title ? (
      <div className="mb-2 flex items-center gap-3">
        <h4 className="text-[11px] font-semibold uppercase tracking-[0.06em] text-ink-500">{title}</h4>
        <span className="h-px flex-1 bg-ink-100" />{aside}
      </div>
    ) : aside ? <div className="mb-2 flex justify-end">{aside}</div> : null}
    {children}
  </section>
);

const blank = (v: ReactNode) => v === null || v === undefined || v === '' || v === false;
/** A label / value grid; a blank value reads "—". */
export function KV({ items }: { items: Array<{ label: string; value: ReactNode; wide?: boolean } | false | null | undefined> }) {
  const rows = items.filter((x): x is { label: string; value: ReactNode; wide?: boolean } => !!x);
  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-2.5 sm:grid-cols-2">
      {rows.map((it) => (
        <div key={it.label} className={`min-w-0 ${it.wide ? 'sm:col-span-2' : ''}`}>
          <dt className="text-[11px] font-medium text-ink-500">{it.label}</dt>
          <dd className="mt-0.5 break-words text-[13px] text-ink-900">{blank(it.value) ? <span className="text-ink-300">—</span> : it.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** One row of a KV / FieldTable list; a falsy entry is skipped. */
export type KvItem = { label: string; value: ReactNode; wide?: boolean };

/**
 * The drawers' Overview as one continuous table of field name | value (3 Nov 2026 — the user: "one table, no titles, no
 * lines between groups, two side by side so four columns show"). The list splits in half — the left table takes the first
 * half (the odd one out). Each half is its own outlined, rounded body with a gap between the two, and from `sm` up the two
 * share one set of row tracks (CSS subgrid), so row 3 on the left lines up with row 3 on the right whatever wraps; below
 * `sm` the right body sits under the left. A blank value says "Not added" in words, never a dash; a long value (an address,
 * a note) wraps and shows in full. A `wide` item stays in the flow and simply wraps. A chip (a ReactNode value) renders as
 * given. The label cells carry the sidebar's tint (bg-ink-50, AppLayout's aside — 3 Nov 2026) so the field names read as a
 * title column apart from the values; the body's overflow-hidden keeps the tint inside the rounded corners.
 */
export function FieldTable({ items }: { items: Array<KvItem | false | null | undefined> }) {
  const rows = items.filter((x): x is KvItem => !!x);
  const half = Math.ceil(rows.length / 2);
  const parts = [rows.slice(0, half), rows.slice(half)].filter((p) => p.length);
  const body = (part: KvItem[], offset: number) => (
    <div key={offset} className="grid grid-cols-[36%_1fr] overflow-hidden rounded-xl border border-ink-200 bg-white sm:grid-rows-subgrid" style={{ gridRow: `span ${part.length}` }}>
      {part.map((it, i) => {
        const cell = `px-3 py-2 ${i < part.length - 1 ? 'border-b border-ink-100' : ''}`;
        return (
          <Fragment key={`${offset + i}-${it.label}`}>
            <div className={`${cell} bg-ink-50 text-[12px] font-medium text-ink-500`}>{it.label}</div>
            <div className={`${cell} min-w-0 break-words whitespace-pre-wrap text-ink-900`}>{blank(it.value) ? <span className="italic text-ink-400">Not added</span> : it.value}</div>
          </Fragment>
        );
      })}
    </div>
  );
  return (
    <div className="grid grid-cols-1 items-start gap-4 text-[13px] sm:grid-cols-2" style={{ gridTemplateRows: `repeat(${half}, auto)` }}>
      {parts.map((p, i) => body(p, i * half))}
    </div>
  );
}

// ── The application form, read-only ─────────────────────────────────────────────

type J = Record<string, any>;
const yesNo = (v: unknown) => (v === true ? 'Yes' : v === false ? 'No' : null);
const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? '' : 's'}`;
/** 30 → "2 years 6 months"; 8 → "8 months"; 0 → null. */
const months = (m: unknown) => { const n = Number(m) || 0; const y = Math.floor(n / 12); return n ? [y ? plural(y, 'year') : '', n % 12 ? plural(n % 12, 'month') : ''].filter(Boolean).join(' ') : null; };
const money = (minor: unknown) => (typeof minor === 'number' && minor > 0 ? gbp(minor) : null);
const NotGiven = () => <p className="text-[12.5px] text-ink-400">Not given yet</p>;
const has = (o: unknown) => !!o && typeof o === 'object' && Object.values(o as J).some((v) => !blank(v as ReactNode) && !(Array.isArray(v) && !v.length));

/**
 * The application form JSON read-only: About you, Addresses, Household, The move (rent offered against the advert, and the
 * preferred viewing times when present), Money, Previous landlord, Right to Rent, Consents. A blank section says so. The
 * groups carry no headings since 3 Nov 2026 (the user: titles off, the rows stay) and no rule either — only space between
 * them; Addresses gets a small label of its own instead, one address a line with its time there in words.
 */
export function ReadOnlyForm({ form, advertMinor, mode = 'apply', headings = true }: { form: J | null | undefined; advertMinor?: number | null; mode?: 'viewing' | 'apply'; /** The admin drawers show the rows without section headings (3 Nov 2026); the tenant's own page keeps them. */ headings?: boolean }) {
  const h = (t: string) => (headings ? t : undefined);
  if (!form || typeof form !== 'object') return <NotGiven />;
  const you: J = form.aboutYou ?? {}; const hh: J = form.household ?? {}; const mv: J = form.move ?? {};
  const mo: J | null = form.money ?? null; const pl: J | null = form.previousLandlord ?? null; const rtr: J | null = form.rightToRent ?? null;
  const c: J = form.consent ?? {};
  const slots: Array<{ date: string; time?: string; part?: string }> = Array.isArray(form.preferredSlots) ? form.preferredSlots : [];
  const offered = typeof mv.offeredRentMinor === 'number' ? mv.offeredRentMinor : null;
  const advert = advertMinor ?? null;
  const offerText = offered == null ? null : !advert ? gbp(offered)
    : offered >= advert ? `${gbp(offered)} (the advert's ${gbp(advert)})`
    : `${gbp(offered)} — ${gbp(advert - offered)} (${(((advert - offered) / advert) * 100).toFixed(1)}%) under the advert's ${gbp(advert)}`;
  const addresses = [you.currentAddress, ...(Array.isArray(you.previousAddresses) ? you.previousAddresses : [])].filter((a: J | undefined) => a?.address);
  const members: J[] = Array.isArray(hh.members) ? hh.members : [];
  const takenBy = typeof c.takenBy === 'string' ? c.takenBy : c.takenBy?.name ?? null;
  const consentRow = (label: string, at: unknown) => ({ label, value: typeof at === 'string' && at ? fmtStamp(at) : null });
  const employment = EMPLOYMENT_STATUSES.find((e) => e.key === mo?.employmentStatus)?.label ?? mo?.employmentStatus ?? null;

  return (
    <div>
      <Section title={h('About you')}>
        <KV items={[
          { label: 'Name', value: [you.title, you.fullName].filter(Boolean).join(' ') },
          { label: 'Date of birth', value: you.dateOfBirth ? fmtDate(you.dateOfBirth) : null },
          { label: 'Email', value: you.email }, { label: 'Mobile', value: formatPhone(you.mobile) },
        ]} />
      </Section>
      <Section title={h('Addresses')}>
        {!headings && <div className="text-[11px] font-medium text-ink-500">Addresses</div>}
        {!addresses.length ? <NotGiven /> : (
          <ul className={`space-y-1 text-[13px] text-ink-900 ${headings ? '' : 'mt-0.5'}`}>
            {addresses.map((a: J, i) => (
              <li key={i}>
                {a.address}{months(a.months) ? <span className="text-ink-500"> · {months(a.months)}</span> : null}
                <span className="ml-1.5 text-[11px] font-medium text-ink-400">{i === 0 && you.currentAddress?.address ? 'current' : 'previous'}</span>
              </li>
            ))}
          </ul>
        )}
      </Section>
      <Section title={h('Household')}>
        {!members.length && !hh.pets && hh.smoker == null ? <NotGiven /> : (
          <>
            {members.length > 0 && (
              <ul className="mb-2 space-y-1 text-[13px] text-ink-900">
                {members.map((m, i) => <li key={i}>{m.name}<span className="text-ink-500">{[m.relationship, m.dateOfBirth ? `born ${fmtDate(m.dateOfBirth)}` : null, m.adult === false ? 'child' : 'adult'].filter(Boolean).map((x) => ` · ${x}`).join('')}</span></li>)}
              </ul>
            )}
            <KV items={[
              { label: 'People moving in', value: String(1 + members.length) },
              { label: 'Pets', value: hh.pets ? (hh.pets.has ? (hh.pets.detail || 'Yes') : 'No') : null },
              { label: 'Smoker', value: yesNo(hh.smoker) },
            ]} />
          </>
        )}
      </Section>
      <Section title={h('The move')}>
        {!has(mv) && !slots.length ? <NotGiven /> : (
          <KV items={[
            { label: 'Move-in date', value: mv.moveInDate ? fmtDate(mv.moveInDate) : null },
            { label: 'Rent offered a month', value: offerText, wide: !!offered && !!advert && offered < advert },
            !!mv.offerReason && { label: 'Why less than the advert', value: mv.offerReason, wide: true },
            { label: 'Parking', value: mv.parkingRequired ? 'Required' : mv.parkingRequired === false ? 'Not needed' : null },
            { label: 'Furniture', value: mv.furnitureRequired ? 'Required (extra cost)' : mv.furnitureRequired === false ? 'Not needed' : null },
            (slots.length > 0 || mode === 'viewing') && {
              label: 'When suits them for a viewing', wide: true,
              value: slots.length ? <span className="flex flex-wrap gap-1">{slots.map((s) => <SlotChip key={`${s.date}${s.time ?? s.part}`} slot={s} />)}</span> : null,
            },
          ]} />
        )}
      </Section>
      <Section title={h('Money')}>
        {!has(mo) ? <NotGiven /> : (
          <KV items={[
            { label: 'Employment', value: employment }, { label: 'Employer', value: mo!.employer },
            { label: 'Job title', value: mo!.jobTitle }, { label: 'Started', value: mo!.startDate ? fmtDate(mo!.startDate) : null },
            { label: 'Annual income', value: money(mo!.annualIncomeMinor) }, { label: 'Other income', value: money(mo!.otherIncomeMinor) },
            { label: 'Benefits a year', value: money(mo!.benefitsMinor) },
            { label: 'Guarantor', value: mo!.guarantor ? (mo!.guarantor.has ? [mo!.guarantor.name, mo!.guarantor.email].filter(Boolean).join(' · ') || 'Yes' : 'No') : null },
          ]} />
        )}
      </Section>
      <Section title={h('Previous landlord')}>
        {!pl ? <NotGiven /> : pl.firstTimeRenter ? <p className="text-[13px] text-ink-900">First time renting</p> : (
          <KV items={[
            { label: 'Landlord or agent', value: pl.name }, { label: 'Email', value: pl.email }, { label: 'Phone', value: formatPhone(pl.phone) },
            { label: 'How long', value: pl.howLong }, { label: 'Rent arrears', value: yesNo(pl.arrears) },
          ]} />
        )}
      </Section>
      <Section title={h('Right to Rent')}>
        {!has(rtr) ? <NotGiven /> : <KV items={[{ label: 'Nationality', value: rtr!.nationality }, { label: 'Share code', value: rtr!.shareCode }]} />}
      </Section>
      <Section title={h('Consents')}>
        {!c.privacyNoticeAt && !c.referencingConsentAt && !c.declarationAt ? <NotGiven /> : (
          <KV items={[
            consentRow(mode === 'viewing' ? 'May be contacted' : 'Privacy notice', c.privacyNoticeAt),
            mode !== 'viewing' || c.referencingConsentAt ? consentRow('Referencing and credit check', c.referencingConsentAt) : null,
            mode !== 'viewing' || c.declarationAt ? consentRow('Declaration', c.declarationAt) : null,
            { label: 'Signed as', value: c.signatureName },
            { label: 'Taken by', value: takenBy ?? (c.privacyNoticeAt ? 'Themselves' : null) },
          ]} />
        )}
      </Section>
    </div>
  );
}

/**
 * The same form as one flat list for the admin drawers' FieldTable (3 Nov 2026): every row ReadOnlyForm shows, in the same
 * order with the same labels and wording, but no sections — a row with nothing in it stays and reads "Not added". Rows the
 * form never had are left out the way the sections leave them out: no landlord rows for a first-time renter (one row says
 * so), no "Why less than the advert" without an offer under it, the viewing-only consents only when given. People moving
 * in and Addresses are one row each with a line per person / address.
 */
export function readOnlyFormItems(form: J | null | undefined, advertMinor?: number | null, mode: 'viewing' | 'apply' = 'apply'): KvItem[] {
  if (!form || typeof form !== 'object') return [];
  const you: J = form.aboutYou ?? {}; const hh: J = form.household ?? {}; const mv: J = form.move ?? {};
  const mo: J = form.money ?? {}; const pl: J | null = form.previousLandlord ?? null; const rtr: J = form.rightToRent ?? {};
  const c: J = form.consent ?? {};
  const slots: Array<{ date: string; time?: string; part?: string }> = Array.isArray(form.preferredSlots) ? form.preferredSlots : [];
  const offered = typeof mv.offeredRentMinor === 'number' ? mv.offeredRentMinor : null;
  const advert = advertMinor ?? null;
  const offerText = offered == null ? null : !advert ? gbp(offered)
    : offered >= advert ? `${gbp(offered)} (the advert's ${gbp(advert)})`
    : `${gbp(offered)} — ${gbp(advert - offered)} (${(((advert - offered) / advert) * 100).toFixed(1)}%) under the advert's ${gbp(advert)}`;
  const addresses = [you.currentAddress, ...(Array.isArray(you.previousAddresses) ? you.previousAddresses : [])].filter((a: J | undefined) => a?.address);
  const members: J[] = Array.isArray(hh.members) ? hh.members : [];
  const takenBy = typeof c.takenBy === 'string' ? c.takenBy : c.takenBy?.name ?? null;
  const consentRow = (label: string, at: unknown): KvItem => ({ label, value: typeof at === 'string' && at ? fmtStamp(at) : null });
  const employment = EMPLOYMENT_STATUSES.find((e) => e.key === mo.employmentStatus)?.label ?? mo.employmentStatus ?? null;
  const people = [String(1 + members.length),
    ...members.map((m) => [m.name, m.relationship, m.dateOfBirth ? `born ${fmtDate(m.dateOfBirth)}` : null, m.adult === false ? 'child' : 'adult'].filter(Boolean).join(' · '))].join('\n');
  const addressLines = addresses.map((a: J, i) => [a.address, months(a.months), i === 0 && you.currentAddress?.address ? 'current' : 'previous'].filter(Boolean).join(' · ')).join('\n');

  const rows: Array<KvItem | false | null> = [
    { label: 'Name', value: [you.title, you.fullName].filter(Boolean).join(' ') },
    { label: 'Date of birth', value: you.dateOfBirth ? fmtDate(you.dateOfBirth) : null },
    { label: 'Email', value: you.email }, { label: 'Mobile', value: formatPhone(you.mobile) },
    { label: 'Addresses', value: addressLines },
    { label: 'People moving in', value: people },
    { label: 'Pets', value: hh.pets ? (hh.pets.has ? (hh.pets.detail || 'Yes') : 'No') : null },
    { label: 'Smoker', value: yesNo(hh.smoker) },
    { label: 'Move-in date', value: mv.moveInDate ? fmtDate(mv.moveInDate) : null },
    { label: 'Rent offered a month', value: offerText },
    !!mv.offerReason && { label: 'Why less than the advert', value: mv.offerReason },
    { label: 'Parking', value: mv.parkingRequired ? 'Required' : mv.parkingRequired === false ? 'Not needed' : null },
    { label: 'Furniture', value: mv.furnitureRequired ? 'Required (extra cost)' : mv.furnitureRequired === false ? 'Not needed' : null },
    (slots.length > 0 || mode === 'viewing') && {
      label: 'When suits them for a viewing',
      value: slots.length ? <span className="flex flex-wrap gap-1">{slots.map((s) => <SlotChip key={`${s.date}${s.time ?? s.part}`} slot={s} />)}</span> : null,
    },
    { label: 'Employment', value: employment }, { label: 'Employer', value: mo.employer },
    { label: 'Job title', value: mo.jobTitle }, { label: 'Started', value: mo.startDate ? fmtDate(mo.startDate) : null },
    { label: 'Annual income', value: money(mo.annualIncomeMinor) }, { label: 'Other income', value: money(mo.otherIncomeMinor) },
    { label: 'Benefits a year', value: money(mo.benefitsMinor) },
    { label: 'Guarantor', value: mo.guarantor ? (mo.guarantor.has ? [mo.guarantor.name, mo.guarantor.email].filter(Boolean).join(' · ') || 'Yes' : 'No') : null },
    ...(pl?.firstTimeRenter ? [{ label: 'Previous landlord', value: 'First time renting' }] : [
      { label: 'Landlord or agent', value: pl?.name }, { label: 'Email', value: pl?.email }, { label: 'Phone', value: formatPhone(pl?.phone) },
      { label: 'How long', value: pl?.howLong }, { label: 'Rent arrears', value: yesNo(pl?.arrears) },
    ]),
    { label: 'Nationality', value: rtr.nationality }, { label: 'Share code', value: rtr.shareCode },
    consentRow(mode === 'viewing' ? 'May be contacted' : 'Privacy notice', c.privacyNoticeAt),
    (mode !== 'viewing' || c.referencingConsentAt) && consentRow('Referencing and credit check', c.referencingConsentAt),
    (mode !== 'viewing' || c.declarationAt) && consentRow('Declaration', c.declarationAt),
    { label: 'Signed as', value: c.signatureName },
    { label: 'Taken by', value: takenBy ?? (c.privacyNoticeAt ? 'Themselves' : null) },
  ];
  return rows.filter((x): x is KvItem => !!x);
}

/** "Tue 27 Oct · 10:00–11:00" as a small chip. */
export const SlotChip = ({ slot }: { slot: { date: string; time?: string | null; part?: string | null } }) => (
  <span className="whitespace-nowrap rounded-md border border-ink-200 bg-white px-1.5 py-0.5 text-[11.5px] text-ink-700">{fmtDay(slot.date)} · {slotLabel(slot.time, slot.part)}</span>
);

// ── Telling them ─────────────────────────────────────────────────────────────────

export interface Recipient { name?: string | null; email?: string | null; phone?: string | null }

/** The outbox's answer, in words: Sent / Queued / Draft / Failed (a text without a provider says so plainly). */
export function NotifyOutcome({ item, channel }: { item: OutboxItem; channel?: NotifyChannel }) {
  const to = [item.toName, item.channel === 'SMS' ? formatPhone(item.toPhone) : item.toEmail].filter(Boolean).join(' · ');
  const sms = (channel ?? item.channel) === 'SMS';
  const how = <b className="font-semibold">{sms ? 'Text' : 'Email'}: </b>;
  if (item.status === 'SENT') return <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-[12.5px] text-emerald-800">{how}sent to {to || 'them'}.</p>;
  if (item.status === 'QUEUED') return <p className="rounded-lg border border-ink-200 bg-ink-50 px-3 py-2 text-[12.5px] text-ink-700">{how}queued for {to || 'them'} — it goes out with the next send.</p>;
  if (item.status === 'DRAFT') return <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12.5px] text-amber-800">{how}saved as a draft in the <Link to="/app/outbox" className="font-medium underline">Outbox</Link>{item.unfilled?.length ? ` — these could not be filled: ${item.unfilled.join(', ')}` : ''}.</p>;
  const notConfigured = sms && /NOT_CONFIGURED|not configured|no sms provider/i.test(item.error ?? '');
  return (
    <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12.5px] text-amber-800">
      {how}{notConfigured
        ? <>Texts are not switched on yet — the message is saved in the <Link to="/app/outbox" className="font-medium underline">Outbox</Link> and goes out once the SMS provider is connected.</>
        : <>Not sent{item.error ? `: ${item.error}` : ''}. It is in the <Link to="/app/outbox" className="font-medium underline">Outbox</Link> to try again.</>}
    </p>
  );
}

/** The templates a sender may pick (GET /templates with templates.read; otherwise free text only). */
export function useLetterTemplates(keys?: string[]) {
  const canRead = useAuth((s) => s.can('templates.read'));
  const q = useQuery({ queryKey: ['/templates'], queryFn: templatesApi.list, enabled: canRead, staleTime: 5 * 60_000 });
  const all = q.data ?? [];
  const generic = all.find((t) => t.key === 'generic') ?? null;
  const picked = keys ? keys.map((k) => all.find((t) => t.key === k)).filter((t): t is EmailTemplate => !!t) : all.filter((t) => t.key !== 'generic');
  return { generic, templates: picked };
}


// ── Telling them with an action ────────────────────────────────────────────────

/** Who to tell after an action: by email, by text, both, or nobody (null). */
export type Tell = NotifyChannel | 'BOTH' | null;
/** Every message that went with an action (one outbox item per channel) and what could not be sent. */
export interface TellResult { notifications: OutboxItem[]; errors: string[] }

/** The channels a choice means: BOTH = email then text. */
export const channelsOf = (tell: Tell): NotifyChannel[] => (tell === 'BOTH' ? ['EMAIL', 'SMS'] : tell ? [tell] : []);
/** Several results as one (an API's own letter, then a text). */
export const mergeTell = (...rs: Array<TellResult | null | undefined>): TellResult | null => {
  const all = rs.filter((r): r is TellResult => !!r);
  return all.length ? { notifications: all.flatMap((r) => r.notifications), errors: all.flatMap((r) => r.errors) } : null;
};
/** What a confirm / reschedule answered (its `notification` / `notifyError`) as a TellResult. */
export const fromChange = (r: { notification: OutboxItem | null; notifyError: { message: string } | null }): TellResult =>
  ({ notifications: r.notification ? [r.notification] : [], errors: r.notifyError ? [r.notifyError.message] : [] });

/** One message after the change, through POST /lettings/notify; a failure never undoes the change. */
export async function sendNote(about: NotifyAbout, channel: NotifyChannel, message: string, templateKey?: string): Promise<TellResult> {
  try {
    return { notifications: [await notifyApi.send({ about, channel, templateKey: templateKey ?? 'generic', message: templateKey ? null : message.trim() })], errors: [] };
  } catch (e) {
    return { notifications: [], errors: [`${channel === 'SMS' ? 'Text' : 'Email'}: ${(e as Error).message}`] };
  }
}
/** The same message on every channel the choice means (null for "Don't tell them"). */
export async function tellNote(about: NotifyAbout, tell: Tell, message: string, templateKey?: string): Promise<TellResult | null> {
  const out: TellResult[] = [];
  for (const c of channelsOf(tell)) out.push(await sendNote(about, c, message, templateKey));
  return mergeTell(...out);
}

/**
 * The confirming button of a step that may tell the person: one button with the verb; pressing it asks "Would you like
 * to tell them?" in the same footer — Email · Text · Both · Don't tell them (a way not on record is left out; `none`
 * false drops "Don't tell them"). With neither an email nor a mobile on record it acts at once, telling nobody.
 */
export function TellButtons({ verb, recipient, onGo, pending, disabled, danger, none = true }: {
  verb: string; recipient: Recipient; onGo: (tell: Tell) => void; pending?: boolean; disabled?: boolean; danger?: boolean; none?: boolean;
}) {
  const [asking, setAsking] = useState(false);
  const [which, setWhich] = useState<Tell | 'NONE' | null>(null);
  const go = (t: Tell) => { setWhich(t ?? 'NONE'); onGo(t); };
  const label = (t: Tell | 'NONE', text: string) => (pending && which === t ? 'Working…' : text);
  const email = !!recipient.email, phone = !!recipient.phone;
  if (!email && !phone) return <Button variant={danger ? 'danger' : 'primary'} disabled={disabled || pending} onClick={() => go(null)}>{label('NONE', verb)}</Button>;
  if (!asking) return <Button variant={danger ? 'danger' : 'primary'} disabled={disabled || pending} onClick={() => setAsking(true)}>{verb}</Button>;
  return (
    <>
      <span className="text-[12.5px] font-medium text-ink-700">Would you like to tell them?</span>
      {email && <Button variant={danger ? 'danger' : 'primary'} disabled={disabled || pending} onClick={() => go('EMAIL')}>{label('EMAIL', 'Email')}</Button>}
      {phone && <Button variant="secondary" disabled={disabled || pending} onClick={() => go('SMS')}>{label('SMS', 'Text')}</Button>}
      {email && phone && <Button variant="secondary" disabled={disabled || pending} onClick={() => go('BOTH')}>{label('BOTH', 'Both')}</Button>}
      {none && <Button variant="secondary" disabled={disabled || pending} onClick={() => go(null)}>{label('NONE', "Don't tell them")}</Button>}
    </>
  );
}

/** The words that go with a text (or an email in your own words): a textarea, pre-filled. */
export function NoteField({ value, onChange, hint, label = 'What they are told (a text, or an email in your words)' }: { value: string; onChange: (v: string) => void; hint?: string; label?: string }) {
  return <Field label={label} hint={hint}><Textarea rows={3} value={value} onChange={(e) => onChange(e.target.value)} className="w-full" maxLength={1000} /></Field>;
}

/** What an action did, shown on the record afterwards: a title, a line, and the outbox's answer about the message. */
export interface Notice { title: string; body?: ReactNode; result?: TellResult | null }
const NOTICE_MS = 8000;

/**
 * The record's notice bar state: set it when an action completes (and the step closes); it clears itself after a few
 * seconds. Clear it (set null) when the next action opens.
 */
export function useNotice() {
  const [notice, setNotice] = useState<Notice | null>(null);
  useEffect(() => {
    if (!notice) return;
    const t = window.setTimeout(() => setNotice(null), NOTICE_MS);
    return () => window.clearTimeout(t);
  }, [notice]);
  return [notice, setNotice] as const;
}

/** The notice at the top of the record's body: the title in bold, the line under it, each message's outcome, and ✕. */
export function NoticeBar({ notice, onClose }: { notice: Notice; onClose: () => void }) {
  return (
    <div role="status" className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50/70 px-3.5 py-2.5">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1 text-[13px] text-emerald-900 [&_p]:text-emerald-900">
          <b className="font-semibold">{notice.title}</b>
          {notice.body && <div className="mt-0.5">{notice.body}</div>}
        </div>
        <button type="button" onClick={onClose} aria-label="Dismiss" className="-mr-1 -mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded text-emerald-800 hover:bg-emerald-100"><X className="h-3.5 w-3.5" /></button>
      </div>
      {(notice.result?.notifications.length || notice.result?.errors.length) ? (
        <div className="mt-2 space-y-1.5">
          {(notice.result?.notifications ?? []).map((n) => <NotifyOutcome key={n.id} item={n} />)}
          {(notice.result?.errors ?? []).map((e) => <p key={e} className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12.5px] text-amber-800">Saved, but no message went — {e}</p>)}
        </div>
      ) : null}
    </div>
  );
}

/** A result screen inside a step (Ping on Offers' Decide): what was done, and the outbox's answer. Footer: Back. */
export function DoneStep({ title, children, result, onDone }: { title: string; children?: ReactNode; result?: TellResult | null; onDone: () => void }) {
  return (
    <DrawerStep title={title} onBack={onDone} back={false} actions={<Button onClick={onDone}>Back</Button>}>
      {children}
      {(result?.notifications ?? []).map((n) => <NotifyOutcome key={n.id} item={n} />)}
      {(result?.errors ?? []).map((e) => <p key={e} className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12.5px] text-amber-800">Saved, but no message went — {e}</p>)}
    </DrawerStep>
  );
}

/**
 * "Message them…" (closed records) and Ping (an approver on Offers) as a step: template or your own words, subject
 * (email), the message; the footer sends by email or by SMS. The outbox's answer replaces the step.
 */
export function MessageStep({ about, recipient, templates: keys, defaultMessage = '', title = 'Message them', onBack, onSent }: {
  about: NotifyAbout; recipient: Recipient; templates?: string[]; defaultMessage?: string; title?: string; onBack: () => void;
  /** Sent: back to the record with the outbox's answer as its notice (without it, a result screen with Back). */
  onSent?: (r: TellResult) => void;
}) {
  const { generic, templates } = useLetterTemplates(keys);
  const [templateKey, setTemplateKey] = useState('generic');
  const tpl = templateKey === 'generic' ? generic : templates.find((t) => t.key === templateKey) ?? null;
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState(defaultMessage);
  const send = useMutation({
    mutationFn: (channel: NotifyChannel) => notifyApi.send({
      about, channel, templateKey, message: templateKey === 'generic' ? message.trim() : null,
      subject: channel === 'EMAIL' ? subject.trim() || undefined : undefined,
    }),
    onSuccess: (item) => onSent?.({ notifications: [item], errors: [] }),
  });
  if (send.data && !onSent) return <DoneStep title="Message sent" result={{ notifications: [send.data], errors: [] }} onDone={onBack} />;
  const ready = templateKey !== 'generic' || !!message.trim();
  const preview = tpl && templateKey !== 'generic' ? tpl.body : null;
  return (
    <DrawerStep title={title} onBack={onBack}
      intro={<>To <b className="font-medium text-ink-900">{recipient.name || 'them'}</b>{[recipient.email, recipient.phone].filter(Boolean).length ? ` · ${[recipient.email, formatPhone(recipient.phone)].filter(Boolean).join(' · ')}` : ''}</>}
      actions={<>
        {recipient.email && <Button icon="Send" onClick={() => send.mutate('EMAIL')} disabled={!ready || send.isPending}>{send.isPending && send.variables === 'EMAIL' ? 'Sending…' : 'Send email'}</Button>}
        {recipient.phone && <Button variant="secondary" icon="MessageSquare" onClick={() => send.mutate('SMS')} disabled={!ready || send.isPending || (templateKey !== 'generic' && !tpl?.smsBody)}>{send.isPending && send.variables === 'SMS' ? 'Sending…' : 'Send SMS'}</Button>}
      </>}>
      {templates.length > 0 && (
        <Field label="Template">
          <Select value={templateKey} onChange={(e) => { setTemplateKey(e.target.value); send.reset(); }} className="w-full">
            <option value="generic">Write my own</option>
            {templates.map((t) => <option key={t.key} value={t.key}>{t.name}</option>)}
          </Select>
        </Field>
      )}
      <Field label="Subject (email)"><Input value={subject} onChange={(e) => setSubject(e.target.value)} className="w-full" placeholder="Leave blank for the flat and development" /></Field>
      {templateKey === 'generic'
        ? <Field label="Message"><Textarea rows={6} value={message} onChange={(e) => setMessage(e.target.value)} className="w-full" placeholder="Write your message — a text is cut to the first 480 characters" maxLength={4000} /></Field>
        : <Field label="Their wording" hint="The merge fields ({{…}}) are filled in when it is sent."><div className="max-h-48 overflow-y-auto whitespace-pre-wrap rounded-lg border border-ink-200 bg-ink-50/60 px-3 py-2 text-[12.5px] text-ink-700">{preview}</div></Field>}
      {!recipient.email && !recipient.phone && <p className="text-[12.5px] text-ink-400">No email or mobile on record.</p>}
      <FormError error={send.error} />
    </DrawerStep>
  );
}
